"use client";

import z from "zod";
import clsx from "clsx";
import {useRouter} from "next/navigation";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {ReCaptchaProvider, useReCaptcha} from "@/components/providers/client/ReCaptchaProvider";
import {Locale, zLang} from "~/i18n-config";
import {zCountry, zPhone} from "~/utils/validation";
import {Controller, FormProvider, SubmitErrorHandler, SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {PhoneField} from "~/components/forms/PhoneField";
import {TextField} from "~/components/Forms";
import {EmailField} from "~/components/forms/EmailField";
import {PasswordField} from "~/components/forms/PasswordField";
import {SubmitButton} from "~/components/forms/SubmitButton";
import {localizePathname} from "@/utils/locales";
import {RecaptchaAction} from "@/utils/google/utils";
import type {SignupType} from "@/utils/account/signup/type";

export function SignupForm(
    { lang, country, dictionary, light, assetId, type, successPathname } : {
        lang: Locale;
        country: string;
        dictionary: any;
        light?: boolean;
        assetId?: string;
        type?: SignupType;
        successPathname?: string;
    }
) {

    const [formType, setFormType] = useState<"phone" | "email">("phone");

    return (
        <ReCaptchaProvider>
            <div className="w-full max-w-xl text-center shadow-2xl shadow-gray-900/10 rounded-xl">
                <div className="flex flex-row overflow-hidden select-none">
                    <div onClick={() => setFormType("phone")}
                         className={clsx(
                             formType === "phone" ?
                                 (light ? "bg-zinc-100 font-semibold text-zinc-700" : "font-semibold bg-white/30 text-zinc-900") :
                                 (light ? "bg-zinc-50 font-normal text-zinc-700" : "font-normal bg-white/50 text-zinc-700"),
                             "w-1/2 rounded-t-xl flex items-center justify-center leading-tight px-4 py-3 cursor-pointer select-none"
                         )}>
                        <span className="hidden md:block">{dictionary.signupByPhone}</span>
                        <span className="block sm:hidden">{dictionary.signupByPhoneMobile}</span>
                    </div>
                    <div onClick={() => setFormType("email")}
                         className={clsx(
                             formType === "email" ?
                                 (light ? "bg-zinc-100 font-semibold text-zinc-700" : "font-semibold bg-white/30 text-zinc-900") :
                                 (light ? "bg-zinc-50 font-normal text-zinc-700" : "font-normal bg-white/50 text-zinc-700"),
                             "w-1/2 rounded-t-xl flex items-center justify-center leading-tight px-4 py-3 cursor-pointer select-none"
                         )}>
                        <span className="hidden md:block">{dictionary.signupByEmailAndPassword}</span>
                        <span className="block sm:hidden">{dictionary.signupByEmailAndPasswordMobile}</span>
                    </div>
                </div>
                <div className={clsx("px-5 md:px-8 py-6 rounded-b-xl", (light ? "bg-zinc-100" : "bg-white/30"))}>
                    {formType === "phone" &&
                        <SignupPhoneForm lang={lang} country={country} dictionary={dictionary} light={light} assetId={assetId} type={type} successPathname={successPathname} />
                    }
                    {formType === "email" &&
                        <SignupEmailAndPasswordForm lang={lang} country={country} dictionary={dictionary} light={light} assetId={assetId} type={type} successPathname={successPathname} />
                    }
                </div>
            </div>
        </ReCaptchaProvider>
    )

}

export function SignupPhoneForm(
    { lang, country, dictionary, light, assetId, type, successPathname } : {
        lang: Locale;
        country: string;
        dictionary: any;
        light?: boolean;
        assetId?: string;
        type?: SignupType;
        successPathname?: string;
    }
) {

    const router = useRouter();
    const { containerId, loaded, isReCaptchaVisible, reCaptchaState, executeReCaptcha, renderReCaptcha, refreshReCaptcha, resetReCaptcha } = useReCaptcha();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isTokenSent, setIsTokenSent] = useState<boolean>(false);

    const tokenInputRef = useRef<HTMLInputElement>(null);
    const sentPhoneRef = useRef<{country: string; phone: string} | null>(null);

    const formSchema = useMemo(() => {
        return z.object({
            lang: zLang(dictionary.validationErrorInvalidLang),
            country: z.string().optional(),
            phone: z.string().optional(),
            token: z.string().optional(),
        }).superRefine((data, ctx) => {
            if (isTokenSent) {
                if (!data.token?.trim()) {
                    ctx.addIssue({
                        path: ["token"],
                        code: z.ZodIssueCode.custom,
                        message: dictionary.validationErrorPhoneTokenRequired,
                    });
                }
                return;
            }

            const countryValidation = zCountry(dictionary.validationErrorInvalidCountry)
                .safeParse(data.country);
            if (!countryValidation.success) {
                ctx.addIssue({
                    path: ["country"],
                    code: z.ZodIssueCode.custom,
                    message: countryValidation.error.issues[0].message,
                });
                return;
            }

            const phoneValidation = zPhone(countryValidation.data, dictionary.validationErrorInvalidPhone);
            const result = phoneValidation.safeParse(data.phone);
            if (!result.success) {
                ctx.addIssue({
                    path: ["phone"],
                    code: z.ZodIssueCode.custom,
                    message: result.error.issues[0].message,
                });
            }
        });

    }, [dictionary, isTokenSent]);

    type FormSchema = z.infer<typeof formSchema>;

    const form= useForm<FormSchema>({
        resolver: zodResolver(formSchema),
        mode: "onBlur",
        defaultValues: {
            lang: lang,
            country: country,
            phone: "",
            token: "",
        }
    });

    const onSendTokenSubmit: SubmitHandler<FormSchema> = async(data) => {

        if (!data.country || !data.phone) {
            return;
        }

        setIsLoading(true);

        const response = await fetch("/auth/login/phone/sendToken", {
            method: "POST",
            body: JSON.stringify({
                ...reCaptchaState,
                ...data,
                ...(assetId ? {signupAssetId: assetId} : {}),
            }),
            headers: {
                "Content-Type": "application/json"
            }
        });

        const result = await response.json();

        if(result.error) {
            setIsLoading(false);
            if(result.errorCaptchaValidation) {
                await renderReCaptcha(containerId);
            } else {
                await refreshReCaptcha(RecaptchaAction.SEND_TOKEN);
            }
            form.setError("phone", {
                type: "manual",
                message: result.errorMessage,
            });
        } else {
            setIsLoading(false);
            sentPhoneRef.current = {
                country: data.country,
                phone: data.phone,
            };
            await resetReCaptcha(RecaptchaAction.VERIFY_TOKEN);
            setIsTokenSent(true);
            setTimeout(() => {
                tokenInputRef.current?.focus();
            }, 3);
        }

    };

    const onVerifyTokenSubmit: SubmitHandler<FormSchema> = async(data) => {

        const sentPhone = sentPhoneRef.current;
        if (!sentPhone) {
            form.setError("phone", {
                type: "manual",
                message: dictionary.validationErrorInvalidPhone,
            });
            return;
        }

        setIsLoading(true);

        const response = await fetch(
            assetId
                ? `/auth/login/${assetId}/phone/verifyToken`
                : "/auth/login/phone/verifyToken",
            {
                method: "POST",
                body: JSON.stringify({
                    ...reCaptchaState,
                    ...data,
                    ...sentPhone,
                    ...(assetId ? {assetId} : {}),
                    ...(type ? {type} : {}),
                }),
                headers: {
                    "Content-Type": "application/json"
                }
            },
        );

        const result = await response.json();

        if(result.error) {
            setIsLoading(false);
            if(result.errorCaptchaValidation) {
                await renderReCaptcha(containerId);
            } else {
                await refreshReCaptcha(RecaptchaAction.VERIFY_TOKEN);
            }
            form.setError("token", {
                type: "manual",
                message: result.errorMessage,
            });
        } else {
            router.replace(result.redirectUrl || localizePathname(
                lang,
                successPathname ?? (assetId ? `/asset/${assetId}/signup/general` : "/dashboard"),
            ));
            setTimeout(() => {
                router.refresh();
            }, 0);
        }

    };

    const onError: SubmitErrorHandler<FormSchema> = (
        errors,
        e
    ) => {
        console.error(errors);
    };

    useEffect(() => {
        if (!loaded) return;
        const runReCaptcha = async () => {
            await executeReCaptcha(RecaptchaAction.SEND_TOKEN);
        };
        runReCaptcha();
    }, [loaded, executeReCaptcha]);

    return(
        <FormProvider {...form}>

            <form
                noValidate
                onSubmit={form.handleSubmit(
                    isTokenSent ? onVerifyTokenSubmit : onSendTokenSubmit,
                    onError,
                )}>

                <div className="grid grid-cols-1 sm:grid-cols-6 gap-y-4 gap-x-4 pb-6">

                    <input type="hidden" value={lang} {...form.register("lang")} />
                    <input type="hidden" value={country} {...form.register("country")} />

                    <Controller
                        control={form.control}
                        name="phone"
                        render={({field: {name, onChange, onBlur, value}, fieldState: {error}}) => {
                            return (
                                <PhoneField
                                    name={name}
                                    lang={lang}
                                    defaultCountry={country}
                                    label={dictionary.signupPhone}
                                    dictionary={{
                                        formCountriesSuggested: dictionary.formCountriesSuggested,
                                        formCountriesOthers: dictionary.formCountriesOthers,
                                    }}
                                    placeholder={dictionary.signupPhonePlaceholder}
                                    onBlur={onBlur}
                                    onValueChangeAction={({phone, country}) => {
                                        form.setValue("country", country);
                                        onChange(phone);
                                    }}
                                    error={error?.message}
                                    defaultValue={value}
                                    light={light}
                                    tabIndex={1}
                                    disabled={isTokenSent}
                                    autoComplete="tel-national"
                                    className="sm:col-span-6"
                                    required/>
                            );
                        }}
                    />

                    {isTokenSent &&
                        <Controller
                            control={form.control}
                            name="token"
                            render={({field: {name, onChange, onBlur, value}, fieldState: {error}}) => {
                                return (
                                    <TextField
                                        ref={tokenInputRef}
                                        name={name}
                                        label={dictionary.signupPhoneToken}
                                        placeholder={dictionary.signupPhoneTokenPlaceholder}
                                        onBlur={onBlur}
                                        onChange={onChange}
                                        error={error?.message}
                                        defaultValue={value}
                                        light={light}
                                        tabIndex={2}
                                        autoComplete="one-time-code"
                                        className="sm:col-span-6"
                                        required/>
                                );
                            }}
                        />
                    }

                    <div className={clsx(isReCaptchaVisible ? "visible" : "hidden", "flex items-center justify-center sm:col-span-6")}>
                        <div id={containerId}></div>
                    </div>

                </div>

                {!isTokenSent ? (
                    <SubmitButton
                        label={dictionary.signupButtonSendToken}
                        disabled={!loaded || !reCaptchaState.captcha} pending={isLoading}
                    />
                ) : (
                    <SubmitButton
                        label={dictionary.signupButtonValidateToken}
                        disabled={!loaded || !reCaptchaState.captcha} pending={isLoading}
                    />
                )}

            </form>

        </FormProvider>
);

}

export function SignupEmailAndPasswordForm(
    { lang, country, dictionary, light, assetId, type, successPathname } : {
        lang: Locale;
        country: string;
        dictionary: any;
        light?: boolean;
        assetId?: string;
        type?: SignupType;
        successPathname?: string;
    }
) {

    const router = useRouter();
    const { containerId, loaded, isReCaptchaVisible, reCaptchaState, executeReCaptcha, renderReCaptcha, refreshReCaptcha } = useReCaptcha();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const formSchema = z.object({
        lang: z.string(),
        country: zCountry(dictionary.validationErrorInvalidCountry),
        email: z
            .string({
                invalid_type_error: dictionary.validationErrorTypeString,
                required_error: dictionary.validationErrorEmailRequired,
            })
            .trim()
            .email({
                message: dictionary.validationErrorEmailRequired,
            }),
        password: z
            .string({
                invalid_type_error: dictionary.validationErrorTypeString,
                required_error: dictionary.validationErrorEmailRequired,
            })
            .trim()
            .min(6, dictionary.validationErrorLengthMin.replace("{min}", 6))
            .regex(/[a-z]/, dictionary.validationErrorPasswordLowercase)
            .regex(/[A-Z]/, dictionary.validationErrorPasswordUppercase)
            .regex(/[0-9]/, dictionary.validationErrorPasswordDigit)
            .regex(/[^a-zA-Z0-9]/, dictionary.validationErrorPasswordSymbol),
    });

    type FormSchema = z.infer<typeof formSchema>;

    const form= useForm<FormSchema>({
        resolver: zodResolver(formSchema),
        mode: "onBlur",
        defaultValues: {
            lang: lang,
            country: country,
            email: "",
            password: "",
        }
    });

    const onSubmit: SubmitHandler<FormSchema> = async (data) => {

        setIsLoading(true);

        const response = await fetch("/auth/signup/email", {
            method: "POST",
            body: JSON.stringify({
                ...reCaptchaState,
                ...data,
                ...(assetId ? {signupAssetId: assetId} : {}),
                ...(type ? {type} : {}),
            }),
            headers: {
                "Content-Type": "application/json"
            }
        });

        const result = await response.json();

        if(result.error) {
            setIsLoading(false);
            if(result.errorCaptchaValidation) {
                await renderReCaptcha(containerId);
            } else {
                await refreshReCaptcha(RecaptchaAction.SIGNUP);
            }
            form.setError("password", {
                type: "manual",
                message: result.errorMessage,
            });
        } else {
            router.replace(result.redirectUrl || localizePathname(
                lang,
                successPathname ?? (assetId ? `/asset/${assetId}/signup/general` : "/dashboard"),
            ));
            setTimeout(() => {
                router.refresh();
            }, 0);
        }

    };

    const onError: SubmitErrorHandler<FormSchema> = (
        errors,
        e
    ) => {
        console.error(errors);
    };

    useEffect(() => {
        if (!loaded) return;
        const runReCaptcha = async () => {
            await executeReCaptcha(RecaptchaAction.SIGNUP);
        };
        runReCaptcha();
    }, [loaded, executeReCaptcha]);

    return(
        <FormProvider {...form}>

            <form>

                <div className="grid grid-cols-1 sm:grid-cols-6 gap-y-4 gap-x-4 pb-6">

                    <input type="hidden" value={lang} {...form.register("lang")} />
                    <input type="hidden" value={country} {...form.register("country")} />

                    <Controller
                        control={form.control}
                        name="email"
                        rules={{required: true}}
                        render={({field: {onChange, onBlur, value}, fieldState: {error}}) => {
                            return (
                                <EmailField
                                    label={dictionary.signupEmail}
                                    placeholder={dictionary.signupEmailPlaceholder}
                                    name="email"
                                    tabIndex={1}
                                    onBlur={onBlur}
                                    onChange={onChange}
                                    error={error?.message}
                                    defaultValue={value}
                                    light={light}
                                    autoComplete="username"
                                    className="sm:col-span-6"
                                    required/>
                            );
                        }}
                    />

                    <Controller
                        control={form.control}
                        name="password"
                        rules={{required: true}}
                        render={({field: {onChange, onBlur, value}, fieldState: {error}}) => {
                            return (
                                <PasswordField
                                    label={dictionary.signupPassword}
                                    placeholder={dictionary.signupPasswordPlaceholder}
                                    name="password"
                                    tabIndex={2}
                                    onBlur={onBlur}
                                    onChange={onChange}
                                    error={error?.message}
                                    defaultValue={value}
                                    light={light}
                                    autoComplete="new-password"
                                    className="sm:col-span-6"
                                    required/>
                            );
                        }}
                    />

                    <div className={clsx(isReCaptchaVisible ? "visible" : "hidden", "flex items-center justify-center sm:col-span-6")}>
                        <div id={containerId}></div>
                    </div>

                </div>

                <SubmitButton
                    label={dictionary.signupButton}
                    onClickAction={() =>
                        form.handleSubmit(onSubmit, onError)()
                    }
                    disabled={!loaded || !reCaptchaState.captcha}
                    pending={isLoading}/>

            </form>

        </FormProvider>
    );

}
